import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Toggle } from '@/components/ui/toggle';
import React, { useEffect } from 'react';
import { getAllModules } from '../utils/common';
import { useTenancy } from '@/contexts/TenancyContext';
import { Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

function RelatedClausesDropdown({ modulesList, setModulesList }) {
  const { activeTenancy } = useTenancy();
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (!activeTenancy || !activeTenancy.id || ["", "Loading..."].includes(activeTenancy.id)) return;
    loadModules();
  }, [activeTenancy]); 

  const loadModules = async () => {
    const allModules = await getAllModules();
    const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

    allModules.forEach(module => {
      module.visible = storedVisibilities[module.id] ?? false;
    });

    setModulesList(allModules);
    setLoading(false);
  }

  const handleToggle = (moduleId, isVisible) => {
    const updated = modulesList.map((m) =>
      m.id === moduleId ? { ...m, visible: isVisible } : m
    );
    setModulesList(updated);

    const visibilityMap = Object.fromEntries(updated.map(m => [m.id, m.visible]));
    localStorage.setItem('modulesVisibility', JSON.stringify(visibilityMap));
  };

  if (loading) {
    return (
      <Button variant="ghost" size="icon" disabled>
        <Settings className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon"
          className="backdrop-blur-md bg-white/80 hover:bg-white dark:bg-secondary/90 dark:hover:bg-card/90 transition-all duration-200 ease-in-out shadow-sm hover:shadow-lg hover:shadow-primary/30 hover:scale-105 text-gray-800 dark:text-gray-100"
        >
          <Settings className="h-4 w-4" />
          <span className="sr-only">Related Clauses</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 p-0 bg-white/95 dark:bg-secondary/95 backdrop-blur-lg border border-gray-200 dark:border-gray-700/80 shadow-2xl shadow-black/10 dark:shadow-black/30 rounded-xl overflow-hidden"
      >
        <div className="px-4 py-3 text-sm font-semibold text-gray-800 dark:text-gray-100 bg-white/90 dark:bg-secondary/90 border-b border-gray-100 dark:border-gray-700/80">
          Related Clauses
        </div>
        <ScrollArea className="h-60">
          <div className="p-2">
            {modulesList.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">
                No modules available
              </div>
            ) : (
              modulesList.map((module) => (
                <DropdownMenuItem 
                  key={module.id} 
                  className={`flex items-center justify-between p-3 rounded-lg transition-all duration-150 m-1 ${
                    module.visible 
                      ? 'bg-primary/10 dark:bg-primary/10 border border-primary/20 dark:border-primary/20 focus:bg-primary/20 dark:focus:bg-primary/20' 
                      : 'hover:bg-gray-100/80 dark:hover:bg-card/50 border border-transparent focus:bg-gray-100/80 dark:focus:bg-card/50'
                  }`}
                  onSelect={(e) => { 
                    e.preventDefault(); 
                    handleToggle(module.id, !module.visible); 
                  }}
                >
                  <span className={`text-sm font-medium ${
                    module.visible 
                      ? 'text-primary dark:text-gray-100' 
                      : 'text-gray-700 dark:text-gray-200'
                  }`}>
                    {module.id} - {module.name}
                  </span>
                  <div className="flex items-center">
                    <div className={`w-9 h-5 flex items-center rounded-full p-0.5 transition-all duration-200 ${
                      module.visible 
                        ? 'bg-primary/90 dark:bg-primary/80 justify-end' 
                        : 'bg-gray-200/80 dark:bg-gray-600/60 justify-start'
                    }`}>
                      <div className="w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200" />
                    </div>
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </div>
        </ScrollArea>
        <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 text-right border-t border-gray-100 dark:border-gray-700/80 bg-white/80 dark:bg-secondary/80">
          {modulesList.filter(m => m.visible).length} of {modulesList.length} active
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export { RelatedClausesDropdown as RelatedClausesAccordianItem };
export default RelatedClausesDropdown;